/* IndustrialPro Automation — content catalog (CommonJS, single source of truth).
   Used by the React app (default import) AND the sitemap generator script. */

const PHONE = "+91 91001 61605";
const PHONE_TEL = "+919100161605";
const WHATSAPP = "919100161605";
const EMAIL = "support@industrialpro.co.in";
const SITE = (typeof process !== "undefined" && process.env && process.env.REACT_APP_SITE_URL) || "https://industrialpro.co.in";
const HQ = "Hyderabad, Telangana, India";

const ACRONYMS = new Set(["plc","vfd","hmi","scada","cnc","spm","iiot","iot","dc","ac","amc","ac/dc","pcb","ups","smps","dcs","mes","opc","ec","evfd","igbt","mcc","pdb","hvac","opcua","mcb","erp","cad"]);
const cap = (s) => s.replace(/\b[\w/]+\b/g, (w) => {
  const key = w.toLowerCase();
  if (ACRONYMS.has(key)) return w.toUpperCase();
  return w.charAt(0).toUpperCase() + w.slice(1);
});
const nameFromSlug = (s) => cap(s.replace(/-/g, " "));

// deterministic picker so generated copy is stable + varied per slug
const seedOf = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};
const pick = (arr, slug, salt = 0) => arr[(seedOf(slug) + salt) % arr.length];

/* ---------------- SERVICES (50) ---------------- */
const SERVICE_CATEGORIES = [
  {
    slug: "plc-automation",
    name: "PLC & Industrial Automation",
    tagline: "Programming, repair, migration and lifecycle support for every major PLC platform.",
    services: [
      "plc-programming", "plc-troubleshooting", "plc-hardware-repair",
      "plc-migration-upgrade", "plc-commissioning", "machine-automation",
      "industrial-automation-solutions", "motion-control-systems",
      "servo-tuning", "plc-preventive-maintenance",
    ],
  },
  {
    slug: "vfd-servo-motor",
    name: "VFD, Servo & Motor Control",
    tagline: "Variable frequency drives, servo systems and motor control — repaired and tuned.",
    services: [
      "vfd-repair", "vfd-programming", "servo-drive-repair", "servo-motor-repair",
      "soft-starter-repair", "dc-drive-repair", "spindle-drive-repair",
      "motor-control-panel", "drive-parameter-setup", "vfd-installation",
    ],
  },
  {
    slug: "scada-hmi-iiot",
    name: "SCADA, HMI & IIoT",
    tagline: "Visualisation, supervisory control and connected-plant systems.",
    services: [
      "scada-development", "hmi-programming", "hmi-repair", "iiot-solutions",
      "data-acquisition-systems", "remote-monitoring", "scada-migration",
      "hmi-touchscreen-repair", "industrial-networking", "energy-monitoring-systems",
    ],
  },
  {
    slug: "control-panels",
    name: "Electrical Control Panels",
    tagline: "Design, manufacturing, repair and retrofitting of industrial control panels.",
    services: [
      "control-panel-manufacturing", "control-panel-repair", "mcc-panel", "plc-panel",
      "vfd-panel", "apfc-panel", "power-distribution-panel", "panel-wiring-services",
      "panel-retrofitting", "synchronization-panel",
    ],
  },
  {
    slug: "cnc-maintenance",
    name: "CNC & Industrial Maintenance",
    tagline: "CNC controls, breakdown response and planned maintenance programmes.",
    services: [
      "cnc-repair", "cnc-controller-repair", "cnc-retrofit", "cnc-maintenance",
      "breakdown-maintenance", "preventive-maintenance", "amc-services",
      "industrial-maintenance", "machine-overhauling", "spare-parts-supply",
    ],
  },
];

/* ---------------- SPM (60) ---------------- */
const SPM_GROUPS = [
  {
    slug: "rebuilding-retrofitting", name: "Machine Rebuilding & Retrofitting",
    services: ["machine-rebuilding", "machine-retrofitting", "cnc-retrofitting", "plc-retrofit",
      "drive-retrofit", "control-system-upgrade", "machine-modernization", "legacy-machine-upgrade",
      "hydraulic-to-servo-retrofit", "relay-to-plc-conversion", "machine-refurbishment",
      "second-hand-machine-retrofit"],
  },
  {
    slug: "maintenance-support", name: "Maintenance & Support",
    services: ["spm-maintenance", "spm-amc", "spm-breakdown-support", "spm-spares",
      "spm-operator-training", "spm-commissioning", "spm-relocation", "spm-installation",
      "spm-troubleshooting", "spm-preventive-maintenance", "spm-performance-audit", "spm-remote-support"],
  },
  {
    slug: "used-machine-services", name: "Used Machine Services",
    services: ["used-machine-buying", "used-machine-selling", "used-machine-inspection",
      "used-machine-valuation", "used-cnc-machines", "used-spm-machines", "machine-de-installation",
      "machine-reinstallation", "machine-buyback", "machine-exchange", "used-machine-warranty"],
  },
  {
    slug: "spm-categories", name: "SPM Categories",
    services: ["assembly-automation", "inspection-machine", "machining-spm", "material-handling-automation",
      "welding-automation", "packaging-machine", "process-automation-machine", "leak-testing-machine",
      "vision-inspection-system", "pick-and-place-machine", "robotic-assembly-cell", "conveyor-automation",
      "part-marking-machine", "testing-rig", "gluing-dispensing-machine", "riveting-machine",
      "pressing-machine", "screwing-station", "palletizing-system", "bottling-line", "capping-machine",
      "labeling-machine", "cartoning-machine", "end-of-line-automation", "feeder-bowl-system"],
  },
];

/* ---------------- POWER ELECTRONICS (47) ---------------- */
const PE_GROUPS = [
  {
    slug: "pcb-repair", name: "PCB Repair",
    services: ["pcb-repair", "smt-rework", "bga-rework", "component-level-repair", "pcb-reverse-engineering",
      "pcb-testing", "multilayer-pcb-repair", "conformal-coating", "pcb-refurbishment"],
  },
  {
    slug: "power-electronics-repair", name: "Power Electronics Repair",
    services: ["power-electronics-repair", "industrial-electronics-repair", "power-supply-repair",
      "smps-repair", "ups-repair", "rectifier-repair", "igbt-module-repair", "thyristor-repair",
      "capacitor-bank-repair", "inverter-card-repair"],
  },
  {
    slug: "drive-inverter-refurbishment", name: "Drive & Inverter Refurbishment",
    services: ["drive-refurbishment", "inverter-refurbishment", "vfd-refurbishment",
      "servo-drive-refurbishment", "dc-drive-refurbishment", "drive-board-repair",
      "inverter-igbt-replacement", "drive-firmware-update", "drive-load-testing"],
  },
  {
    slug: "motor-servo-services", name: "Motor & Servo Services",
    services: ["servo-motor-repair-service", "spindle-motor-repair", "encoder-repair", "resolver-repair",
      "motor-rewinding", "servo-motor-testing", "brake-motor-repair", "bldc-motor-repair",
      "motor-feedback-repair"],
  },
  {
    slug: "testing-technical-support", name: "Testing & Technical Support",
    services: ["load-testing", "functional-testing", "thermal-imaging", "insulation-testing",
      "root-cause-analysis", "calibration-services", "technical-consultation", "on-site-diagnostics",
      "warranty-repair", "emergency-electronics-repair"],
  },
];

/* ---------------- LANDING PAGES (20) ---------------- */
const LANDING_PAGES = [
  "plc-repair", "plc-programming", "vfd-repair", "servo-drive-repair", "servo-motor-repair",
  "hmi-repair", "scada-development", "control-panel-manufacturer", "cnc-repair", "pcb-repair",
  "industrial-electronics-repair", "spm-manufacturer", "machine-retrofit", "amc-services",
  "predictive-maintenance", "breakdown-maintenance", "robot-automation", "industrial-iot",
  "motion-control", "industrial-maintenance",
];

/* ---------------- BRANDS ---------------- */
const BRAND_CATEGORIES = [
  "plc", "hmi", "scada", "vfd", "servo", "stepper", "cnc-controllers", "cnc-machines",
  "vmc", "hmc", "robots", "instrumentation", "sensors-vision", "power-supplies",
];
const BRANDS = [
  "siemens", "abb", "allen-bradley", "schneider", "delta", "mitsubishi", "yaskawa", "omron",
  "fanuc", "danfoss", "bosch-rexroth", "fuji", "panasonic", "keyence", "beckhoff", "ls-electric",
  "hitachi", "toshiba", "weg", "inovance", "emerson", "kinco", "xinje", "invt", "veichi",
  "estun", "leadshine", "weintek", "pro-face", "autonics",
];

/* ---------------- INDUSTRIES (13) ---------------- */
const INDUSTRIES = [
  { slug: "automotive", name: "Automotive", note: "High-speed assembly, robotics and zero-defect quality gates." },
  { slug: "pharmaceutical", name: "Pharma", note: "GMP-critical uptime, validated systems and audit-ready documentation." },
  { slug: "food-processing", name: "Food", note: "Hygienic automation, washdown-rated panels and continuous throughput." },
  { slug: "steel", name: "Steel", note: "Heavy drives, rolling mill control and rugged breakdown response." },
  { slug: "textile", name: "Textile", note: "Drive-heavy lines, tension control and energy optimisation." },
  { slug: "packaging", name: "Packaging", note: "End-of-line automation, servo motion and vision inspection." },
  { slug: "plastics", name: "Plastic", note: "Injection & extrusion machine control, temperature loops and servo tuning." },
  { slug: "cement", name: "Cement", note: "Kiln control, heavy drives and continuous plant support." },
  { slug: "chemical", name: "Chemical", note: "Hazardous-area instrumentation, interlocks and safe shutdown logic." },
  { slug: "fmcg", name: "FMCG", note: "High-throughput packaging lines, OEE tracking and rapid changeovers." },
  // Legacy slugs kept for backwards-compat + extra SEO coverage
  { slug: "cement-steel", name: "Cement & Steel", note: "Heavy drives, kiln control and rugged breakdown response." },
  { slug: "plastics-packaging", name: "Plastics & Packaging", note: "Servo motion, temperature control and end-of-line automation." },
  { slug: "water-treatment", name: "Water Treatment", note: "SCADA telemetry, pump automation and remote monitoring." },
];
// Ordered display list for the homepage grid (10 industries per SEO spec)
const HOME_INDUSTRIES = ["automotive","pharmaceutical","food-processing","steel","textile","packaging","plastics","cement","chemical","fmcg"];

/* ---------------- SERVICE AREAS (16) ---------------- */
const AREAS = [
  { slug: "hyderabad", name: "Hyderabad", geo: [17.385, 78.4867], landmark: "HITEC City & the greater Hyderabad industrial belt", industries: "pharma, engineering, electronics and packaging units" },
  { slug: "secunderabad", name: "Secunderabad", geo: [17.4399, 78.4983], landmark: "Secunderabad Cantonment & Balanagar corridor", industries: "engineering workshops and fabrication units" },
  { slug: "jeedimetla", name: "Jeedimetla", geo: [17.5087, 78.4497], landmark: "Jeedimetla Industrial Area (JIEA)", industries: "pharma, bulk drug and chemical plants" },
  { slug: "patancheru", name: "Patancheru", geo: [17.5299, 78.2645], landmark: "Patancheru–Bollaram industrial cluster", industries: "chemical, pharma and heavy engineering" },
  { slug: "uppal", name: "Uppal", geo: [17.4058, 78.5591], landmark: "Uppal & Nacharam industrial estates", industries: "food processing and light engineering" },
  { slug: "bollaram", name: "Bollaram", geo: [17.5449, 78.3498], landmark: "Bollaram Industrial Area", industries: "pharma, chemical and packaging plants" },
  { slug: "medchal", name: "Medchal", geo: [17.6295, 78.4818], landmark: "Medchal industrial corridor", industries: "plastics, packaging and engineering" },
  { slug: "cherlapally", name: "Cherlapally", geo: [17.4735, 78.5844], landmark: "Cherlapally Industrial Estate (IDA)", industries: "engineering, pharma and electronics" },
  { slug: "nacharam", name: "Nacharam", geo: [17.4239, 78.5537], landmark: "Nacharam IDA", industries: "engineering and manufacturing units" },
  { slug: "balanagar", name: "Balanagar", geo: [17.4718, 78.4382], landmark: "Balanagar Industrial Area", industries: "defence, engineering and machine shops" },
  { slug: "sanathnagar", name: "Sanathnagar", geo: [17.4585, 78.4356], landmark: "Sanathnagar Industrial Estate", industries: "engineering and process plants" },
  { slug: "kukatpally", name: "Kukatpally", geo: [17.4948, 78.3996], landmark: "Kukatpally & IDA Gandhinagar", industries: "light manufacturing and packaging" },
  { slug: "genome-valley", name: "Genome Valley", geo: [17.5566, 78.5546], landmark: "Genome Valley life-sciences cluster", industries: "biotech, pharma and R&D facilities" },
  { slug: "pharma-city", name: "Pharma City", geo: [17.1899, 78.6006], landmark: "Hyderabad Pharma City, Mucherla", industries: "bulk drug and API manufacturing" },
  { slug: "ghatkesar", name: "Ghatkesar", geo: [17.4501, 78.6812], landmark: "Ghatkesar industrial zone", industries: "engineering and warehousing" },
  { slug: "shamirpet", name: "Shamirpet", geo: [17.6142, 78.5638], landmark: "Shamirpet & Genome Valley Phase-II", industries: "biotech and precision manufacturing" },
];

/* ---------------- PROGRAMMATIC COMBOS ---------------- */
const PROG_SERVICES = [
  "vfd-repair", "plc-repair", "plc-programming", "servo-drive-repair", "servo-motor-repair",
  "hmi-repair", "scada-development", "control-panel-manufacturer", "cnc-repair", "pcb-repair",
  "industrial-electronics-repair", "drive-repair", "amc-services", "breakdown-maintenance",
  "predictive-maintenance", "machine-retrofit", "spm-manufacturer", "motion-control",
  "industrial-iot", "industrial-maintenance",
];
const PROG_CITIES = AREAS.slice(0, 15).map((a) => a.slug); // 15 cities -> 300 pages

const BRAND_COMBO_BRANDS = [
  "siemens", "abb", "allen-bradley", "schneider", "delta", "mitsubishi", "yaskawa", "omron",
  "fanuc", "danfoss", "bosch-rexroth", "fuji", "inovance", "invt", "ls-electric",
];
const BRAND_COMBO_SERVICES = [
  "vfd-repair", "plc-repair", "plc-programming", "servo-drive-repair", "servo-motor-repair",
  "hmi-repair", "drive-repair", "plc-troubleshooting", "scada-development", "dc-drive-repair",
];

// slug maps for O(1) resolve
const SERVICE_CITY_MAP = {};
PROG_SERVICES.forEach((s) => PROG_CITIES.forEach((c) => { SERVICE_CITY_MAP[`${s}-${c}`] = { service: s, city: c }; }));
const BRAND_SERVICE_MAP = {};
BRAND_COMBO_BRANDS.forEach((b) => BRAND_COMBO_SERVICES.forEach((s) => { BRAND_SERVICE_MAP[`${b}-${s}`] = { brand: b, service: s }; }));

const cityName = (slug) => (AREAS.find((a) => a.slug === slug) || { name: nameFromSlug(slug) }).name;
const brandName = (slug) => {
  const map = { "allen-bradley": "Allen-Bradley", "bosch-rexroth": "Bosch Rexroth", "ls-electric": "LS Electric", "pro-face": "Pro-face", weg: "WEG", abb: "ABB", invt: "INVT", scada: "SCADA", plc: "PLC", hmi: "HMI", vfd: "VFD" };
  return map[slug] || nameFromSlug(slug);
};

/* ---------------- DUPLICATE CANONICALISATION ----------------
   Slugs that live in both LANDING_PAGES (root /vfd-repair) AND under a category
   (/services/vfd-repair, /power-electronics/vfd-repair etc.). We keep the ROOT
   landing page as the SEO-canonical version and 301-redirect the /services/*
   version to it — killing keyword cannibalisation. */
const _allCategoryServices = new Set();
SERVICE_CATEGORIES.forEach((c) => c.services.forEach((s) => _allCategoryServices.add(`/services/${s}`)));
SPM_GROUPS.forEach((g) => g.services.forEach((s) => _allCategoryServices.add(`/spm-solutions/${s}`)));
PE_GROUPS.forEach((g) => g.services.forEach((s) => _allCategoryServices.add(`/power-electronics/${s}`)));

const DUPLICATE_REDIRECTS = {}; // /services/vfd-repair -> /vfd-repair
LANDING_PAGES.forEach((s) => {
  ["/services/", "/spm-solutions/", "/power-electronics/"].forEach((prefix) => {
    const dup = prefix + s;
    if (_allCategoryServices.has(dup)) DUPLICATE_REDIRECTS[dup] = `/${s}`;
  });
});

/* ---------------- STABLE LASTMOD ----------------
   Fixed per section so the same lastmod isn't stamped on every URL every deploy.
   Also uses a slug-hash offset (±30 days) so Google sees natural per-URL variance
   instead of one identical date across the entire section. */
const RELEASE_DATES = {
  root: "2026-07-15",       // homepage & hub pages
  legal: "2026-06-01",      // legal / about / contact
  landing: "2026-07-15",    // high-intent landing pages (main money URLs)
  services: "2026-06-15",   // /services/*
  spm: "2026-06-15",        // /spm-solutions/*
  pe: "2026-06-15",         // /power-electronics/*
  brand: "2026-06-15",      // /brands/*
  industry: "2026-06-01",   // /industries/*
  area: "2026-06-15",       // /service-areas/*
  local: "2026-06-15",      // /{service}-{city}
  brandService: "2026-06-15", // /{brand}-{service}
};

// Deterministic per-slug lastmod: base date +/- (seedOf(slug) % 45) days
// This gives every URL a stable, realistic-looking modification date
// so the sitemap doesn't look programmatically-generated to search engines.
function lastmodFor(base, slug) {
  const offset = (seedOf(slug || "") % 45) - 22; // -22 to +22 days
  const d = new Date(base + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + offset);
  // clamp to the past — never a future lastmod
  const today = new Date();
  if (d > today) d.setTime(today.getTime());
  return d.toISOString().slice(0, 10);
}

/* ================= CONTENT TEMPLATES ================= */
const RESPONSE_TABLE = [
  { level: "Emergency Breakdown", time: "Rapid local response", detail: "Line-down support across Hyderabad & Telangana, subject to availability." },
  { level: "Standard On-site", time: "Same / next day", detail: "Scheduled visit for diagnostics and field repair, subject to scheduling." },
  { level: "Bench Repair (Courier-in)", time: "Typical 3–7 working days", detail: "Component-level repair with functional testing before dispatch." },
  { level: "AMC / Contract", time: "Priority SLA", detail: "Agreed response windows under annual contract." },
];

const INTRO_OPENERS = [
  "When production stops, every minute is measured in lost output.",
  "Every failure has a real cost, and it rarely happens at a convenient time.",
  "A single failed component can halt an entire production line.",
  "Reliable automation is the difference between a good shift and a scrapped one.",
];
const WHY_FAIL = [
  "voltage spikes, capacitor ageing, overheating, dust ingress and vibration",
  "supply transients, cooling-fan failure, dry electrolytic capacitors and loose terminations",
  "IGBT stress, thermal cycling, contamination and firmware corruption",
  "power-quality issues, ageing components, mechanical wear and environmental stress",
];

function faqBlock({ subject, city, brand }) {
  const where = city ? ` in ${city}` : " across Hyderabad & Telangana";
  const who = brand ? `${brand} ` : "";
  return [
    { q: `Do you provide ${subject.toLowerCase()}${where}?`, a: `Yes. IndustrialPro Automation is an independent service provider offering ${subject.toLowerCase()}${where}. We handle emergency breakdowns with a typical 2–4 hour local response in selected Hyderabad areas (subject to availability), plus scheduled and bench repairs. Call ${PHONE} to log a job.` },
    { q: `How fast can you respond to a breakdown?`, a: `For line-down emergencies we aim for a rapid local response within the Hyderabad industrial belt — typically 2–4 hours in selected areas, subject to location, equipment and technician availability. Bench and courier-in repairs are typically completed in 3–7 working days after diagnostics.` },
    { q: `Do you repair ${who}equipment or only replace it?`, a: `We prioritise component-level repair. Our engineers diagnose the root cause, repair the ${who}unit, functionally test it and return it — usually at a fraction of replacement cost. Replacement is recommended only when repair is not economical.` },
    { q: `What warranty do you provide?`, a: `Repair warranty is provided as per the service/repair terms detailed in writing with your service report, covering the fault rectified and workmanship.` },
    { q: `Are you an authorised OEM service centre?`, a: `No. We are an independent repair and service company. Brand names are used only to describe the equipment we service; we are not affiliated with, sponsored by, or authorised by any manufacturer.` },
  ];
}

function processSteps(subject) {
  return [
    { title: "Fault Logging & Triage", text: `Share the fault code, symptoms and equipment details on ${PHONE}. We triage severity and dispatch accordingly.` },
    { title: "Diagnostics", text: `On-site or bench diagnostics isolate the root cause of the ${subject.toLowerCase()} — not just the symptom.` },
    { title: "Repair Estimate", text: "You receive a clear, no-obligation estimate before any chargeable work begins." },
    { title: "Component-Level Repair", text: "Experienced engineers repair to component level using quality-matched parts and controlled soldering." },
    { title: "Functional Testing", text: "Every unit is powered up and tested per equipment requirements before sign-off." },
    { title: "Installation & Commissioning", text: "We refit, parameterise and commission on site, verifying the line runs to spec." },
    { title: "Warranty & Report", text: "You get repair warranty as per our service terms and a written service report for your maintenance records." },
  ];
}

const WARRANTY_TEXT = "Every job is backed by a repair warranty as per the service terms in your written report, transparent pre-approved estimates, and a written report for your CMMS. Clear diagnosis, transparent charges — just equipment that runs.";

function makeIntro(subject, { city, brand } = {}) {
  const slug = `${subject}${city || ""}${brand || ""}`;
  const opener = pick(INTRO_OPENERS, slug, 1);
  const fails = pick(WHY_FAIL, slug, 2);
  const loc = city ? `in ${city} and the surrounding Hyderabad industrial belt` : "across Hyderabad, Telangana and pan-India";
  const brandLine = brand
    ? ` Our engineers regularly service ${brand} equipment, and we maintain the diagnostic know-how and quality-matched parts to restore it quickly. `
    : " ";
  return [
    `${opener} IndustrialPro Automation provides independent, engineer-led ${subject.toLowerCase()} ${loc}. We combine deep hands-on experience with disciplined, component-level repair so your line comes back online fast — and stays online.`,
    `Most failures we see trace back to ${fails}. Rather than swap boards blindly, we diagnose the true root cause, repair to component level where economical, and functionally test every unit per equipment requirements before it returns to service.${brandLine}The result is lower total cost of ownership and fewer repeat failures.`,
    `Backed by a repair warranty per service terms, multi-brand industrial equipment support and rapid local response, IndustrialPro Automation is the maintenance partner Hyderabad's plants call when uptime matters. Call ${PHONE} or request a quote — emergency support available.`,
  ].join("\n\n");
}

function pageBase(path, h1, titleTail, desc) {
  const rawTitle = `${h1} | ${titleTail} | IndustrialPro`.slice(0, 62).replace(/\s*\|\s*$/, "");
  return {
    path,
    seo: {
      h1,
      title: rawTitle,
      description: desc.slice(0, 160),
      canonical: `${SITE}${path}`,
    },
  };
}

/* ---- resolvers return a full descriptor for LandingTemplate ---- */
function serviceDescriptor(slug, category) {
  const name = nameFromSlug(slug);
  const b = pageBase(`/services/${slug}`, name,
    "Independent Service, Rapid Response",
    `${name} by IndustrialPro Automation — independent, component-level repair with rapid local response and repair warranty per service terms. Call ${PHONE}.`);
  const related = siblingLinks(category.services, slug, "/services/")
    .concat(topBrandLinks(null, 5))
    .concat(topCityLinks(null, 4))
    .concat([{ name: "All Services", path: "/services" }, { name: "Request a Quote", path: "/request" }, { name: "Emergency Breakdown", path: "/request" }]);
  return {
    ...b, kind: "service", name,
    breadcrumbs: [{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: category.name, path: "/services" }, { name, path: b.path }],
    intro: makeIntro(name),
    process: processSteps(name),
    responseTable: RESPONSE_TABLE,
    warranty: WARRANTY_TEXT,
    faq: faqBlock({ subject: name }),
    related,
    category: category.name,
  };
}
function groupDescriptor(slug, group, base, pillarName, pillarPath) {
  const name = nameFromSlug(slug);
  const b = pageBase(`${base}/${slug}`, name, "Hyderabad | Independent Repair",
    `${name} — ${pillarName} by IndustrialPro Automation. Independent service, rapid local response, repair warranty across Hyderabad. Call ${PHONE}.`);
  const related = siblingLinks(group.services, slug, base + "/")
    .concat(topBrandLinks(null, 5))
    .concat(topCityLinks(null, 4))
    .concat([{ name: pillarName, path: pillarPath }, { name: "All Services", path: "/services" }, { name: "Request a Quote", path: "/request" }]);
  return {
    ...b, kind: "service", name,
    breadcrumbs: [{ name: "Home", path: "/" }, { name: pillarName, path: pillarPath }, { name: group.name, path: pillarPath }, { name, path: b.path }],
    intro: makeIntro(name),
    process: processSteps(name),
    responseTable: RESPONSE_TABLE,
    warranty: WARRANTY_TEXT,
    faq: faqBlock({ subject: name }),
    related,
    category: group.name,
  };
}
import { LANDING_CONTENT, defaultContentFor } from "./landing-content.mjs";

function landingDescriptor(slug) {
  const name = nameFromSlug(slug);
  const b = pageBase(`/${slug}`, `${name} in Hyderabad`, "Independent Repair, Hyderabad",
    `${name} in Hyderabad by IndustrialPro Automation. Independent experts, rapid local response, multi-brand support, repair warranty per service terms. Call ${PHONE} now.`);
  const related = landingRelated(slug)
    .concat(topBrandLinks(null, 6))
    .concat(topCityLinks(null, 6))
    .concat([{ name: "Service Areas", path: "/service-areas" }, { name: "All Brands", path: "/brands" }]);
  const pack = LANDING_CONTENT[slug] || defaultContentFor(slug, name);
  const extendedFaq = (pack.extendedFaq || []).concat(faqBlock({ subject: name, city: "Hyderabad" }));
  return {
    ...b, kind: "landing", name,
    breadcrumbs: [{ name: "Home", path: "/" }, { name, path: b.path }],
    intro: pack.intro || makeIntro(name, { city: "Hyderabad" }),
    process: processSteps(name),
    responseTable: RESPONSE_TABLE,
    warranty: WARRANTY_TEXT,
    faq: extendedFaq.slice(0, 10),
    // NEW rich sections for content depth
    commonFaults: pack.commonFaults,
    brandsPack: pack.brands,
    industriesPack: pack.industries,
    caseStudy: pack.caseStudy,
    whyChoose: pack.whyChoose,
    related,
    category: "High-Intent Service",
  };
}
function serviceCityDescriptor(serviceSlug, citySlug) {
  const sName = nameFromSlug(serviceSlug);
  const cName = cityName(citySlug);
  const area = AREAS.find((a) => a.slug === citySlug);
  const b = pageBase(`/${serviceSlug}-${citySlug}`, `${sName} in ${cName}`, "Local Service",
    `${sName} in ${cName}? IndustrialPro Automation offers rapid local response, component-level repair & repair warranty near ${area ? area.landmark : cName}. Call ${PHONE}.`);
  const related = PROG_CITIES.filter((c) => c !== citySlug).slice(0, 10).map((c) => ({ name: `${sName} in ${cityName(c)}`, path: `/${serviceSlug}-${c}` }))
    .concat(PROG_SERVICES.filter((s) => s !== serviceSlug).slice(0, 6).map((s) => ({ name: `${nameFromSlug(s)} in ${cName}`, path: `/${s}-${citySlug}` })))
    .concat(topBrandLinks(null, 4))
    .concat([{ name: `${sName} (Overview)`, path: `/${serviceSlug}` }, { name: `${cName} Service Area`, path: `/service-areas/${citySlug}` }, { name: "Request a Quote", path: "/request" }]);
  return {
    ...b, kind: "local", name: `${sName} in ${cName}`, city: cName, geo: area && area.geo,
    // Google Search Console flagged 300+ of these as "duplicate, Google chose different canonical".
    // Serve them for direct hits but tell crawlers to prefer the stronger /{service} + /service-areas/{city} pages.
    noindex: true,
    seo: { ...b.seo, canonical: `${SITE}/${serviceSlug}` },
    breadcrumbs: [{ name: "Home", path: "/" }, { name: "Service Areas", path: "/service-areas" }, { name: cName, path: `/service-areas/${citySlug}` }, { name: sName, path: b.path }],
    intro: makeIntro(sName, { city: cName }) + (area ? `\n\nWe regularly support plants near ${area.landmark}, serving ${area.industries}. NAP: IndustrialPro Automation, ${HQ} — ${PHONE}.` : ""),
    process: processSteps(sName),
    responseTable: RESPONSE_TABLE,
    warranty: WARRANTY_TEXT,
    faq: faqBlock({ subject: sName, city: cName }),
    related,
    category: "Local Service",
  };
}
function brandServiceDescriptor(brandSlug, serviceSlug) {
  const bn = brandName(brandSlug);
  const sName = nameFromSlug(serviceSlug);
  const b = pageBase(`/${brandSlug}-${serviceSlug}`, `${bn} ${sName}`, "Independent Repair",
    `Independent ${bn} ${sName.toLowerCase()} by IndustrialPro Automation. Component-level repair, rapid local response & repair warranty in Hyderabad. Call ${PHONE}.`);
  const related = BRAND_COMBO_SERVICES.filter((s) => s !== serviceSlug).slice(0, 8).map((s) => ({ name: `${bn} ${nameFromSlug(s)}`, path: `/${brandSlug}-${s}` }))
    .concat(BRAND_COMBO_BRANDS.filter((x) => x !== brandSlug).slice(0, 6).map((x) => ({ name: `${brandName(x)} ${sName}`, path: `/${x}-${serviceSlug}` })))
    .concat(topCityLinks(null, 4))
    .concat([{ name: `${bn} (Brand Page)`, path: `/brands/${brandSlug}` }, { name: `${sName} Overview`, path: `/${serviceSlug === "drive-repair" ? "vfd-repair" : serviceSlug}` }, { name: "All Brands", path: "/brands" }, { name: "Request a Quote", path: "/request" }]);
  return {
    ...b, kind: "brand-service", name: `${bn} ${sName}`, brand: bn,
    breadcrumbs: [{ name: "Home", path: "/" }, { name: "Brands", path: "/brands" }, { name: bn, path: `/brands/${brandSlug}` }, { name: sName, path: b.path }],
    intro: makeIntro(sName, { brand: bn }),
    process: processSteps(`${bn} ${sName}`),
    responseTable: RESPONSE_TABLE,
    warranty: WARRANTY_TEXT,
    disclaimer: `“${bn}” is a trademark of its respective owner. IndustrialPro Automation is an independent repair provider and is not affiliated with, sponsored by, or authorised by ${bn}.`,
    faq: faqBlock({ subject: `${bn} ${sName}`, brand: bn }),
    related,
    category: "Brand Service",
  };
}
function brandDescriptor(brandSlug) {
  const bn = brandName(brandSlug);
  const b = pageBase(`/brands/${brandSlug}`, `${bn} Repair & Service`, "Independent, Hyderabad",
    `Independent ${bn} repair & service — PLC, VFD, servo, HMI & drives. IndustrialPro Automation, Hyderabad. Repair warranty per service terms, rapid local response. Call ${PHONE}.`);
  const related = BRAND_COMBO_SERVICES.slice(0, 8).map((s) => ({ name: `${bn} ${nameFromSlug(s)}`, path: `/${brandSlug}-${s}` }))
    .concat(BRANDS.filter((x) => x !== brandSlug).slice(0, 8).map((x) => ({ name: `${brandName(x)} Service`, path: `/brands/${x}` })))
    .concat(topCityLinks(null, 4))
    .concat([{ name: "All Brands", path: "/brands" }, { name: "Popular: VFD Repair", path: "/vfd-repair" }, { name: "Popular: PLC Repair", path: "/plc-repair" }, { name: "Request a Quote", path: "/request" }]);
  return {
    ...b, kind: "brand", name: `${bn} Repair & Service`, brand: bn,
    breadcrumbs: [{ name: "Home", path: "/" }, { name: "Brands", path: "/brands" }, { name: bn, path: b.path }],
    intro: makeIntro(`${bn} equipment repair`, { brand: bn }),
    process: processSteps(`${bn} equipment`),
    responseTable: RESPONSE_TABLE,
    warranty: WARRANTY_TEXT,
    disclaimer: `“${bn}” and related marks are trademarks of their respective owner. IndustrialPro Automation is an independent repair company and is not affiliated with, sponsored by, or authorised by ${bn}. Brand names are used only to identify equipment we service.`,
    faq: faqBlock({ subject: `${bn} repair`, brand: bn }),
    related,
    category: "Brand",
  };
}
function brandCategoryDescriptor(catSlug) {
  const cn = brandName(catSlug);
  const b = pageBase(`/brands/${catSlug}`, `${cn} Brands We Service`, "Independent Repair",
    `${cn} brands serviced by IndustrialPro Automation — independent repair, programming & support in Hyderabad. Repair warranty per service terms. Call ${PHONE}.`);
  const rel = BRANDS.slice(0, 12).map((x) => ({ name: brandName(x), path: `/brands/${x}` }));
  return {
    ...b, kind: "brand-category", name: `${cn} Brands`, category: "Brand Category",
    breadcrumbs: [{ name: "Home", path: "/" }, { name: "Brands", path: "/brands" }, { name: cn, path: b.path }],
    intro: makeIntro(`${cn} systems repair`),
    process: processSteps(`${cn} systems`),
    responseTable: RESPONSE_TABLE,
    warranty: WARRANTY_TEXT,
    disclaimer: "All brand and product names are trademarks of their respective owners. IndustrialPro Automation is independent and not authorised by any OEM.",
    faq: faqBlock({ subject: `${cn} repair` }),
    related: rel.concat([{ name: "All Brands", path: "/brands" }]),
  };
}
function industryDescriptor(ind) {
  const b = pageBase(`/industries/${ind.slug}`, `Automation for ${ind.name}`, "Hyderabad",
    `Industrial automation, repair & maintenance for ${ind.name.toLowerCase()} plants by IndustrialPro Automation, Hyderabad. ${ind.note} Call ${PHONE}.`);
  const related = INDUSTRIES.filter((i) => i.slug !== ind.slug).slice(0, 7).map((i) => ({ name: i.name, path: `/industries/${i.slug}` }))
    .concat(LANDING_PAGES.slice(0, 6).map((s) => ({ name: nameFromSlug(s), path: `/${s}` })))
    .concat(topBrandLinks(null, 4))
    .concat(topCityLinks(null, 4))
    .concat([{ name: "All Industries", path: "/industries" }, { name: "Request a Quote", path: "/request" }]);
  return {
    ...b, kind: "industry", name: `${ind.name} Automation`, category: "Industry",
    breadcrumbs: [{ name: "Home", path: "/" }, { name: "Industries", path: "/industries" }, { name: ind.name, path: b.path }],
    intro: `${ind.note}\n\n` + makeIntro(`${ind.name} plant automation & maintenance`),
    process: processSteps(`${ind.name} automation`),
    responseTable: RESPONSE_TABLE,
    warranty: WARRANTY_TEXT,
    faq: faqBlock({ subject: `${ind.name} automation service` }),
    related,
  };
}
function areaDescriptor(area) {
  const b = pageBase(`/service-areas/${area.slug}`, `Industrial Automation Services in ${area.name}`, "Local Service",
    `Automation repair & maintenance in ${area.name}, Hyderabad by IndustrialPro Automation. Near ${area.landmark}. Rapid local response, repair warranty per service terms. Call ${PHONE}.`);
  const related = PROG_SERVICES.slice(0, 12).map((s) => ({ name: `${nameFromSlug(s)} in ${area.name}`, path: `/${s}-${area.slug}` }))
    .concat(AREAS.filter((a) => a.slug !== area.slug).slice(0, 6).map((a) => ({ name: `Services in ${a.name}`, path: `/service-areas/${a.slug}` })))
    .concat(topBrandLinks(null, 4))
    .concat([{ name: "All Service Areas", path: "/service-areas" }, { name: "All Services", path: "/services" }, { name: "Request a Quote", path: "/request" }]);
  return {
    ...b, kind: "area", name: `${area.name} Service Area`, city: area.name, geo: area.geo, category: "Service Area",
    breadcrumbs: [{ name: "Home", path: "/" }, { name: "Service Areas", path: "/service-areas" }, { name: area.name, path: b.path }],
    intro: `IndustrialPro Automation serves ${area.name} and the area around ${area.landmark}, home to ${area.industries}.\n\n` + makeIntro("industrial automation repair & maintenance", { city: area.name }) + `\n\nNAP: IndustrialPro Automation · ${HQ} · ${PHONE} · ${EMAIL}. Emergency support is available for breakdowns in ${area.name}.`,
    process: processSteps(`${area.name} industrial service`),
    responseTable: RESPONSE_TABLE,
    warranty: WARRANTY_TEXT,
    faq: faqBlock({ subject: "industrial automation service", city: area.name }),
    related,
  };
}

/* ---------------- TOPICAL HUB PAGES ----------------
   Parent hubs at /plc, /vfd, /servo, /scada, /hmi.
   Each hub consolidates topical authority for its child services + brands + fault codes
   + parameter DBs (future). Addresses the "missing hub pages" SEO audit finding by
   giving Google a single high-authority parent URL for each equipment family. */
const HUB_PAGES = {
  plc: {
    name: "PLC",
    fullName: "Programmable Logic Controllers",
    title: "PLC Programming, Repair & Migration",
    tagline: "Everything PLC — programming, hardware repair, migrations & lifecycle support for every major platform.",
    lead:
      "Programmable Logic Controllers (PLCs) are the beating heart of modern industrial automation. From compact micro-PLCs on a single conveyor to distributed I/O racks controlling an entire plant, our team lives inside PLC logic every day. IndustrialPro Automation offers full lifecycle PLC services — new programming, migration off obsolete platforms, hardware and CPU repair, backup & documentation, and emergency breakdown support across Hyderabad and pan-India.",
    body:
      "We work daily with Siemens (S7-200 / S7-300 / S7-400 / S7-1200 / S7-1500 / LOGO!), Allen-Bradley (SLC 500, MicroLogix, CompactLogix, ControlLogix), Mitsubishi (FX, Q, iQ-R), Delta (DVP, AS, AH), Omron (CJ, CP, NX/NJ), Schneider (Modicon M221 / M241 / M340 / M580), and every legacy platform still on Indian shop floors. Whether you need a fresh ladder-logic build, a structured-text migration, a firmware upgrade, or component-level CPU / power-supply / I/O card repair, our engineers have done it before — and we back every job with a repair warranty per service terms and functional testing before reinstallation.",
    services: [
      { name: "PLC Programming", path: "/plc-programming" },
      { name: "PLC Repair", path: "/plc-repair" },
      { name: "PLC Troubleshooting", path: "/services/plc-troubleshooting" },
      { name: "PLC Hardware Repair", path: "/services/plc-hardware-repair" },
      { name: "PLC Migration & Upgrade", path: "/services/plc-migration-upgrade" },
      { name: "PLC Commissioning", path: "/services/plc-commissioning" },
      { name: "PLC Preventive Maintenance", path: "/services/plc-preventive-maintenance" },
      { name: "PLC Panel Manufacturing", path: "/services/plc-panel" },
      { name: "Machine Automation", path: "/services/machine-automation" },
      { name: "Motion Control Systems", path: "/services/motion-control-systems" },
    ],
    brands: ["siemens", "allen-bradley", "mitsubishi", "delta", "omron", "schneider", "abb", "fuji", "ls-electric", "inovance"],
    faq: [
      { q: "Which PLC brands do you service?", a: "We work with Siemens (S7 range, LOGO!), Allen-Bradley (MicroLogix, CompactLogix, ControlLogix), Mitsubishi (FX, Q, iQ-R), Delta (DVP, AS, AH), Omron (CJ, CP, NX/NJ), Schneider Modicon, ABB, Fuji, LS Electric, Inovance and more. Independent service — not affiliated with any OEM." },
      { q: "Do you migrate legacy PLCs to modern platforms?", a: "Yes. We routinely migrate S7-200 → S7-1200, SLC 500 → CompactLogix, Q → iQ-R, obsolete Delta → AS-series and similar upgrades — preserving your existing logic while modernising hardware and firmware." },
      { q: "Can you repair the PLC CPU or do you only replace it?", a: "We repair to component level wherever it's economical — CPU boards, power supply cards, I/O modules, backplanes. Every repair is functionally tested per equipment requirements before dispatch. Replacement is recommended only when repair costs approach replacement cost." },
      { q: "How fast is PLC breakdown response?", a: "Rapid local response within the Hyderabad industrial belt (typically 2–4 hours in selected areas, subject to availability); emergency support available. Courier-in bench repairs typically complete in 3–7 working days after diagnostics." },
      { q: "Do you provide programming backups and documentation?", a: "Yes — every job includes annotated program backups, tag lists, wiring documentation and a written service report you can hand to auditors and future maintenance teams." },
    ],
  },
  vfd: {
    name: "VFD",
    fullName: "Variable Frequency Drives",
    title: "VFD Repair, Programming & Refurbishment",
    tagline: "Component-level VFD repair, drive programming and refurbishment for every major manufacturer.",
    lead:
      "Variable Frequency Drives (VFDs) — also called AC drives, inverters or frequency converters — control almost every motor in a modern plant. When they fail, production stops. IndustrialPro Automation is Hyderabad's independent VFD specialist, offering fast on-site diagnostics, component-level board and IGBT-module repair, parameter setup and drive refurbishment for every major manufacturer.",
    body:
      "We service Siemens SINAMICS (V20, G120, G120C, S120), ABB (ACS150, ACS355, ACS550, ACS580, ACS880), Delta (VFD-M, VFD-B, VFD-E, VFD-C2000, MS300, MH300, CH2000), Mitsubishi (FR-D700, FR-E700, FR-A800), Yaskawa (V1000, A1000, GA500, GA700), Danfoss (VLT), Fuji (FRENIC series), Schneider (Altivar), LS Electric (iS7, iG5A, H100), Inovance (MD/MK series), INVT and more. Common failure modes we address daily: IGBT module burn-out, dry electrolytic capacitors, blown gate-driver boards, tripped DC-bus, corrupted parameters, cooling-fan failure and firmware corruption. Every repair is functionally tested per equipment requirements on our bench before it returns to your line.",
    services: [
      { name: "VFD Repair", path: "/vfd-repair" },
      { name: "VFD Programming", path: "/services/vfd-programming" },
      { name: "VFD Installation", path: "/services/vfd-installation" },
      { name: "VFD Refurbishment", path: "/power-electronics/vfd-refurbishment" },
      { name: "Drive Parameter Setup", path: "/services/drive-parameter-setup" },
      { name: "DC Drive Repair", path: "/services/dc-drive-repair" },
      { name: "Soft Starter Repair", path: "/services/soft-starter-repair" },
      { name: "Spindle Drive Repair", path: "/services/spindle-drive-repair" },
      { name: "VFD Panel Manufacturing", path: "/services/vfd-panel" },
      { name: "Inverter Card Repair", path: "/power-electronics/inverter-card-repair" },
    ],
    brands: ["siemens", "abb", "delta", "mitsubishi", "yaskawa", "danfoss", "fuji", "schneider", "ls-electric", "inovance", "invt", "veichi"],
    faq: [
      { q: "Which VFD brands do you repair?", a: "Siemens SINAMICS, ABB ACS, Delta VFD/MS/MH/CH, Mitsubishi FR, Yaskawa V1000/A1000/GA-series, Danfoss VLT, Fuji FRENIC, Schneider Altivar, LS Electric iS7/iG5A, Inovance, INVT, Veichi — all repaired to component level with warranty per service terms." },
      { q: "What are the most common VFD failure causes?", a: "Voltage spikes and phase imbalance, IGBT thermal stress from cooling-fan failure, aged / dry electrolytic capacitors, blown gate-driver boards, contamination on power boards, and firmware corruption from unstable supply. We diagnose the root cause — not just the symptom." },
      { q: "Do you repair the IGBT power stage or only the control board?", a: "Both. Our engineers replace IGBT modules, rebuild driver stages, reflow damaged tracks and repair control PCBs at component level. Every repair is functionally tested per equipment requirements before dispatch." },
      { q: "How long does a bench VFD repair take?", a: "Typical turnaround is 3–7 working days from arrival, including diagnostics, component-level repair, parameter restoration and testing per equipment requirements. Emergency swap-outs may be possible via our exchange inventory, subject to availability." },
      { q: "Do you supply refurbished VFDs?", a: "Yes — quality-matched refurbished units are available for many popular models with warranty per service terms, ideal when the original OEM has discontinued the part." },
    ],
  },
  servo: {
    name: "Servo",
    fullName: "Servo Drives & Motors",
    title: "Servo Drive Repair, Motor Repair & Tuning",
    tagline: "Component-level servo drive & motor repair, encoder / resolver rebuild and motion tuning.",
    lead:
      "Servo systems demand precision — and precision demands specialists. IndustrialPro Automation offers full-stack servo services: drive electronics repair, motor rewinding and encoder rebuild, brake disassembly, motion tuning, and complete axis commissioning. Whether it's a packaging machine servo, a CNC spindle, or a high-dynamic robotic axis, we bring the diagnostic depth to get it back on spec.",
    body:
      "We service and repair servo drives and motors from Yaskawa (Sigma-5, Sigma-7, Σ-II), Mitsubishi (MR-J2, MR-J3, MR-J4, MR-J5), Siemens (SINAMICS S120, 6SN, 611U), Delta (ASDA-A, A2, A3, B2, B3), Panasonic (MINAS A4, A5, A6), Fanuc (αi, βi), Bosch Rexroth (IndraDrive), Allen-Bradley (Kinetix), Omron (G5, 1S) and more. Common jobs: encoder EEPROM corruption, resolver bearing failure, brake stuck-on, drive DC-bus fault, absolute-position loss, communication (SSCNET, EtherCAT, Mechatrolink, PROFINET) drop-outs, and drive-to-motor combination retuning after a mechanical rebuild.",
    services: [
      { name: "Servo Drive Repair", path: "/servo-drive-repair" },
      { name: "Servo Motor Repair", path: "/servo-motor-repair" },
      { name: "Servo Tuning", path: "/services/servo-tuning" },
      { name: "Servo Motor Repair Service", path: "/power-electronics/servo-motor-repair-service" },
      { name: "Servo Motor Testing", path: "/power-electronics/servo-motor-testing" },
      { name: "Encoder Repair", path: "/power-electronics/encoder-repair" },
      { name: "Resolver Repair", path: "/power-electronics/resolver-repair" },
      { name: "Motor Rewinding", path: "/power-electronics/motor-rewinding" },
      { name: "Motor Feedback Repair", path: "/power-electronics/motor-feedback-repair" },
      { name: "Motion Control Systems", path: "/services/motion-control-systems" },
    ],
    brands: ["yaskawa", "mitsubishi", "siemens", "delta", "panasonic", "fanuc", "bosch-rexroth", "allen-bradley", "omron", "estun"],
    faq: [
      { q: "Which servo brands do you repair?", a: "Yaskawa Sigma-5/7, Mitsubishi MR-J2/J3/J4/J5, Siemens SINAMICS S120 and 611U, Delta ASDA-A/A2/A3/B2/B3, Panasonic MINAS A-series, Fanuc αi/βi, Bosch Rexroth IndraDrive, Allen-Bradley Kinetix, Omron G5/1S and legacy models. Component-level repair with warranty per service terms." },
      { q: "Can you repair the servo motor and encoder together?", a: "Yes. We rebuild bearings, replace seals and brakes, re-solder feedback pigtails, restore absolute-position data on absolute encoders, and pair-tune the drive+motor combination on a dynamometer after any mechanical work." },
      { q: "What is servo tuning and do I need it?", a: "Servo tuning matches drive gains to the load's inertia and stiffness. It's needed after any mechanical change (new gearbox, ballscrew, coupling), after replacing the drive or motor, or when you see hunting, overshoot, or resonance on the axis." },
      { q: "Do you handle CNC spindle motors?", a: "Yes — spindle motor rewinding, thermistor replacement, rotor balancing, bearing replacement and encoder installation are core services." },
      { q: "How do I ship a servo motor for bench repair?", a: "Call +91 91001 61605, log the fault, and we'll share our bench-repair address plus a packing checklist. Turnaround is typically 3–7 working days including functional testing per equipment requirements." },
    ],
  },
  scada: {
    name: "SCADA",
    fullName: "Supervisory Control & Data Acquisition",
    title: "SCADA Development, Migration & Support",
    tagline: "Custom SCADA, alarm engineering, historian setup and legacy-SCADA migration.",
    lead:
      "SCADA is where your plant becomes data. IndustrialPro Automation builds, upgrades and maintains SCADA systems for manufacturing, water treatment, energy and process industries. From a single-station GUI on a PC to a redundant client-server plant SCADA with historian, alarms, reports and remote access — we design, deploy, migrate and support the entire stack.",
    body:
      "We work with Ignition (Inductive Automation), Wonderware / AVEVA System Platform, WinCC / WinCC OA (Siemens), FactoryTalk View SE/ME (Rockwell), iFIX (GE), Citect, InTouch, Zenon, GENESIS64, Movicon and open-stack SCADA on Node-RED / Grafana / InfluxDB / TimescaleDB. Typical scope: PLC-to-SCADA tag mapping, alarm engineering (ISA-18.2 aligned), historian setup, KPI dashboards, MES/ERP data hand-off (OPC UA / MQTT / REST), redundant-server architecture, and secure remote-monitoring VPN. Migration jobs (Wonderware → System Platform, WinCC classic → WinCC Unified) are a specialty.",
    services: [
      { name: "SCADA Development", path: "/scada-development" },
      { name: "SCADA Migration", path: "/services/scada-migration" },
      { name: "HMI Programming", path: "/services/hmi-programming" },
      { name: "IIoT Solutions", path: "/services/iiot-solutions" },
      { name: "Data Acquisition Systems", path: "/services/data-acquisition-systems" },
      { name: "Remote Monitoring", path: "/services/remote-monitoring" },
      { name: "Industrial Networking", path: "/services/industrial-networking" },
      { name: "Energy Monitoring Systems", path: "/services/energy-monitoring-systems" },
    ],
    brands: ["siemens", "schneider", "allen-bradley", "abb", "mitsubishi", "delta", "omron", "beckhoff"],
    faq: [
      { q: "Which SCADA platforms do you develop on?", a: "Ignition, Wonderware / AVEVA System Platform, Siemens WinCC and WinCC Unified, Rockwell FactoryTalk View SE/ME, GE iFIX, Citect, InTouch, Zenon, GENESIS64, Movicon, plus open-stack SCADA on Node-RED, Grafana, InfluxDB and TimescaleDB." },
      { q: "Do you migrate legacy SCADA to modern platforms?", a: "Yes. Common migrations we run: Wonderware InTouch → System Platform, WinCC Classic → WinCC Unified, FactoryTalk SE upgrades, and legacy DOS/Windows-XP SCADA to modern Windows Server or Linux stacks. We preserve tag databases and historical data." },
      { q: "Can you add historian and dashboards to an existing SCADA?", a: "Yes — we retrofit historians (Wonderware Historian, Ignition Tag Historian, InfluxDB, TimescaleDB) and dashboarding (Grafana, Power BI, Ignition Perspective, WinCC Unified) onto existing SCADA and PLC infrastructures without ripping out the runtime." },
      { q: "What about MES / ERP / IIoT integration?", a: "We push data northbound over OPC UA, MQTT (Sparkplug B), REST and SQL — into MES / ERP / Azure / AWS / on-prem data lakes. IIoT gateways (Ignition Edge, Kepware, Node-RED, MoxaConnect) are part of the toolkit." },
      { q: "Do you provide SCADA cyber-security hardening?", a: "Yes — network segmentation, VPN remote access, role-based auth, audit trails, patch management and IEC-62443 aligned hardening are part of every deployment we own." },
    ],
  },
  hmi: {
    name: "HMI",
    fullName: "Human-Machine Interfaces",
    title: "HMI Programming, Repair & Touchscreen Service",
    tagline: "Multi-brand HMI screen design, migration and component-level touchscreen repair.",
    lead:
      "The HMI is where the operator meets the machine — and when it fails, operators fly blind. IndustrialPro Automation offers full HMI services: fresh screen design, project migration between brands, hardware repair of touchscreens, backlights and power circuits, and firmware / OS restoration on locked or corrupted panels.",
    body:
      "We service and program Siemens (KTP, TP177, TP277, Comfort Panel, Unified Panel), Mitsubishi (GOT1000, GOT2000, GT27), Delta (DOP-B, DOP-100, DOP-107), Weintek (MT8000, cMT), Pro-face (GP3000, GP4000, SP5000), Allen-Bradley (PanelView Plus, PanelView 5500), Schneider (Magelis, HMIGT-series), Omron (NB, NS, NA-series), Kinco, Fatek and Autonics HMIs. Repair work covers cracked or ghost-touching resistive/capacitive screens, dim or dead LED backlights, dead SMPS sections, corrupted flash memory, and RS-232 / RS-422 / Ethernet driver-chip replacement. Programming work covers full HMI project builds, tag mapping to PLCs, alarm/event logging, recipe management and multi-language screen design.",
    services: [
      { name: "HMI Programming", path: "/services/hmi-programming" },
      { name: "HMI Repair", path: "/hmi-repair" },
      { name: "HMI Touchscreen Repair", path: "/services/hmi-touchscreen-repair" },
      { name: "SCADA Development", path: "/scada-development" },
      { name: "Industrial Networking", path: "/services/industrial-networking" },
      { name: "Data Acquisition Systems", path: "/services/data-acquisition-systems" },
    ],
    brands: ["siemens", "mitsubishi", "delta", "weintek", "pro-face", "allen-bradley", "schneider", "omron", "kinco", "autonics"],
    faq: [
      { q: "Which HMI brands do you program and repair?", a: "Siemens (KTP, TP, Comfort, Unified), Mitsubishi GOT1000/GOT2000/GT27, Delta DOP-B/100/107, Weintek MT8000/cMT, Pro-face GP3000/4000, Allen-Bradley PanelView Plus, Schneider Magelis, Omron NB/NS/NA, Kinco, Fatek, Autonics — programming and repair, brand-independent." },
      { q: "Can you fix a cracked or ghost-touching HMI screen?", a: "Yes. We replace resistive and capacitive touch overlays, LCD panels and backlight units, and re-flash the panel firmware. Faster than an OEM RMA and typically half the cost." },
      { q: "Do you migrate an HMI project from one brand to another?", a: "Yes — we routinely port projects (e.g., Delta DOP → Weintek, GOT1000 → GOT2000, PanelView → PanelView Plus) while preserving screen layouts, tag structures and alarm history." },
      { q: "Do you unlock or recover locked / password-protected HMIs?", a: "For legitimate ownership, we recover HMI firmware and project uploads on many models. Proof of ownership is required." },
      { q: "How long does HMI repair take?", a: "Diagnostics in 24–48 hours after arrival; typical repair turnaround 3–7 working days. On-site swap and reinstallation available in Hyderabad." },
    ],
  },
};

function hubDescriptor(slug) {
  const h = HUB_PAGES[slug];
  if (!h) return null;
  const b = pageBase(`/${slug}`, h.title, "Hyderabad · Pan-India",
    `${h.tagline} Independent, rapid local response and repair warranty by IndustrialPro Automation, Hyderabad. Call ${PHONE}.`);

  // Rich related pack: services + brands + cities + fault-code hint (~25 links)
  const related = h.services.slice()
    .concat(h.brands.slice(0, 8).map((b) => ({ name: `${brandName(b)} ${h.name} Service`, path: `/brands/${b}` })))
    .concat(topCityLinks(null, 6))
    .concat([
      { name: "All Services", path: "/services" },
      { name: "All Brands", path: "/brands" },
      { name: "Service Areas", path: "/service-areas" },
      { name: "Request a Quote", path: "/request" },
    ]);

  return {
    ...b, kind: "hub", name: h.title, category: `${h.name} Hub`,
    breadcrumbs: [{ name: "Home", path: "/" }, { name: h.name, path: `/${slug}` }],
    intro: `${h.lead}\n\n${h.body}\n\nEvery job is backed by a repair warranty per service terms, functionally tested reinstallation, and a written service report. Call ${PHONE} for emergency breakdown support across Hyderabad, Telangana and pan-India.`,
    process: processSteps(`${h.name} equipment`),
    responseTable: RESPONSE_TABLE,
    warranty: WARRANTY_TEXT,
    faq: h.faq,
    related,
    hub: h, // expose raw config in case a custom hub template wants to render sub-sections
  };
}

const HUB_SLUGS = Object.keys(HUB_PAGES);

function siblingLinks(list, current, prefix) {
  return list.filter((s) => s !== current).slice(0, 10).map((s) => ({ name: nameFromSlug(s), path: prefix + s }));
}

function landingRelated(slug) {
  return LANDING_PAGES.filter((s) => s !== slug).slice(0, 12).map((s) => ({ name: nameFromSlug(s), path: `/${s}` }))
    .concat([{ name: "All Services", path: "/services" }, { name: "Request a Quote", path: "/request" }]);
}

/* Contextual link boosters — used to reach ~15–25 internal links per page */
function popularHyderabadServices(excludePath) {
  return LANDING_PAGES.slice(0, 8)
    .map((s) => ({ name: `${nameFromSlug(s)} in Hyderabad`, path: `/${s}` }))
    .filter((l) => l.path !== excludePath);
}
function topBrandLinks(exclude = null, n = 6) {
  return BRANDS.filter((b) => b !== exclude).slice(0, n)
    .map((b) => ({ name: `${brandName(b)} Service`, path: `/brands/${b}` }));
}
function topCityLinks(exclude = null, n = 6) {
  return AREAS.filter((a) => a.slug !== exclude).slice(0, n)
    .map((a) => ({ name: `Services in ${a.name}`, path: `/service-areas/${a.slug}` }));
}

/* ---------------- MASTER RESOLVER ---------------- */
function findService(slug) {
  for (const c of SERVICE_CATEGORIES) if (c.services.includes(slug)) return c;
  return null;
}
function findGroup(groups, slug) {
  for (const g of groups) if (g.services.includes(slug)) return g;
  return null;
}
function resolvePage(pathname) {
  let p = pathname.replace(/\/+$/, "") || "/";
  p = p.toLowerCase();

  // 301-style redirect for duplicates → keep the LANDING page as canonical
  if (DUPLICATE_REDIRECTS[p]) {
    return { redirectTo: DUPLICATE_REDIRECTS[p], path: p };
  }

  const parts = p.split("/").filter(Boolean);

  if (parts[0] === "services" && parts[1]) {
    const cat = findService(parts[1]);
    if (cat) return serviceDescriptor(parts[1], cat);
  }
  if (parts[0] === "spm-solutions" && parts[1]) {
    const g = findGroup(SPM_GROUPS, parts[1]);
    if (g) return groupDescriptor(parts[1], g, "/spm-solutions", "SPM Solutions", "/spm-solutions");
  }
  if (parts[0] === "power-electronics" && parts[1]) {
    const g = findGroup(PE_GROUPS, parts[1]);
    if (g) return groupDescriptor(parts[1], g, "/power-electronics", "Power Electronics", "/power-electronics");
  }
  if (parts[0] === "industries" && parts[1]) {
    const ind = INDUSTRIES.find((i) => i.slug === parts[1]);
    if (ind) return industryDescriptor(ind);
  }
  if (parts[0] === "service-areas" && parts[1]) {
    const area = AREAS.find((a) => a.slug === parts[1]);
    if (area) return areaDescriptor(area);
  }
  if (parts[0] === "brands" && parts[1]) {
    if (BRAND_CATEGORIES.includes(parts[1])) return brandCategoryDescriptor(parts[1]);
    if (BRANDS.includes(parts[1])) return brandDescriptor(parts[1]);
  }
  if (parts.length === 1) {
    const s = parts[0];
    if (HUB_SLUGS.includes(s)) return hubDescriptor(s);
    if (LANDING_PAGES.includes(s)) return landingDescriptor(s);
    if (SERVICE_CITY_MAP[s]) return serviceCityDescriptor(SERVICE_CITY_MAP[s].service, SERVICE_CITY_MAP[s].city);
    if (BRAND_SERVICE_MAP[s]) return brandServiceDescriptor(BRAND_SERVICE_MAP[s].brand, BRAND_SERVICE_MAP[s].service);
  }
  return null;
}

/* ---------------- SITEMAP URLS ---------------- */
function getAllUrls() {
  const urls = [];
  const add = (loc, priority, changefreq, lastmod) => urls.push({ loc: SITE + loc, priority, changefreq, lastmod });

  // ---- Homepage ----
  add("/", "1.0", "weekly", RELEASE_DATES.root);

  // ---- Main hubs (index landing pages) ----
  ["/services", "/spm-solutions", "/power-electronics", "/brands", "/industries", "/service-areas"]
    .forEach((u) => add(u, "0.9", "weekly", RELEASE_DATES.root));

  // ---- Utility & legal (rarely change) ----
  ["/about", "/contact"].forEach((u) => add(u, "0.6", "yearly", RELEASE_DATES.legal));
  ["/request"].forEach((u) => add(u, "0.7", "yearly", RELEASE_DATES.legal));
  ["/sitemap", "/privacy-policy", "/terms-of-service", "/legal/trademarks"]
    .forEach((u) => add(u, "0.3", "yearly", RELEASE_DATES.legal));

  // ---- High-intent landing pages (main money URLs) ----
  LANDING_PAGES.forEach((s) => add(`/${s}`, "0.9", "weekly", lastmodFor(RELEASE_DATES.landing, s)));

  // ---- Topical hubs (parent authority pages) ----
  HUB_SLUGS.forEach((s) => add(`/${s}`, "0.9", "weekly", lastmodFor(RELEASE_DATES.landing, s)));

  // ---- Category service pages (varied priority + monthly refresh) ----
  SERVICE_CATEGORIES.forEach((c) => c.services.forEach((s) => {
    if (!DUPLICATE_REDIRECTS[`/services/${s}`]) add(`/services/${s}`, "0.7", "monthly", lastmodFor(RELEASE_DATES.services, s));
  }));
  SPM_GROUPS.forEach((g) => g.services.forEach((s) => {
    if (!DUPLICATE_REDIRECTS[`/spm-solutions/${s}`]) add(`/spm-solutions/${s}`, "0.6", "monthly", lastmodFor(RELEASE_DATES.spm, s));
  }));
  PE_GROUPS.forEach((g) => g.services.forEach((s) => {
    if (!DUPLICATE_REDIRECTS[`/power-electronics/${s}`]) add(`/power-electronics/${s}`, "0.6", "monthly", lastmodFor(RELEASE_DATES.pe, s));
  }));

  // ---- Brand pages ----
  BRAND_CATEGORIES.forEach((c) => add(`/brands/${c}`, "0.7", "monthly", lastmodFor(RELEASE_DATES.brand, c)));
  BRANDS.forEach((b) => add(`/brands/${b}`, "0.7", "monthly", lastmodFor(RELEASE_DATES.brand, b)));

  // ---- Industries (topical breadth pages) ----
  INDUSTRIES.forEach((i) => add(`/industries/${i.slug}`, "0.6", "yearly", lastmodFor(RELEASE_DATES.industry, i.slug)));

  // ---- Service areas (local SEO, priority tier 2) ----
  AREAS.forEach((a) => add(`/service-areas/${a.slug}`, "0.7", "monthly", lastmodFor(RELEASE_DATES.area, a.slug)));

  // ---- Programmatic Service×City (long-tail) ----
  // Excluded from sitemap on 2026-08 after Google Search Console reported
  // 1,645 as "duplicate, Google chose different canonical". Pages still resolve
  // for direct hits but emit noindex,follow so they drop out of the index.
  // Real geographic SEO layer is /service-areas/{city} (16 unique pages).
  // Object.keys(SERVICE_CITY_MAP).forEach((s) => add(`/${s}`, "0.5", "monthly", lastmodFor(RELEASE_DATES.local, s)));

  // ---- Programmatic Brand×Service (long-tail commercial) ----
  Object.keys(BRAND_SERVICE_MAP).forEach((s) => add(`/${s}`, "0.6", "monthly", lastmodFor(RELEASE_DATES.brandService, s)));

  return urls;
}

const catalog = {
  PHONE, PHONE_TEL, WHATSAPP, EMAIL, SITE, HQ,
  nameFromSlug, brandName, cityName,
  SERVICE_CATEGORIES, SPM_GROUPS, PE_GROUPS, LANDING_PAGES,
  BRAND_CATEGORIES, BRANDS, INDUSTRIES, AREAS, HOME_INDUSTRIES,
  HUB_PAGES, HUB_SLUGS,
  PROG_SERVICES, PROG_CITIES, RESPONSE_TABLE,
  DUPLICATE_REDIRECTS, RELEASE_DATES,
  resolvePage, getAllUrls,
};

export default catalog;
